//voteComment.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Typography, Button, Snackbar, Alert } from "@mui/material";
import { useForm, FormProvider } from "react-hook-form";
import { useState } from "react";
import { FormTextField } from "../../../components/FormComponent/FormComponent";
import { useMediaQuery, useTheme } from "@mui/material";
import { ApplicationData } from "../ApplicationData";

type CommentFormType = {
  comment: string;
};

type ApprovalCommentBoxProps = {
  handleApproveVote: (comment: string) => void;
  handleRejectVote: (comment: string) => void;
  handleSendBack: (comment: string) => void;
  applicationData: ApplicationData;
};

/**
 * 承認コメント入力コンポーネント
 * - 承認・却下・差戻しボタンとコメント入力欄を表示する
 * - props: handleApproveVote (承認処理), handleRejectVote (却下処理),
 *          handleSendBack (差戻し処理), applicationData (申請データ)
 */
export const ApprovalCommentBox = ({
  handleApproveVote,
  handleRejectVote,
  handleSendBack,
  applicationData,
}: ApprovalCommentBoxProps) => {
  const form = useForm<CommentFormType>({ defaultValues: { comment: "" } });
  const { handleSubmit, reset } = form;
  const [decisionType, setDecisionType] = useState<
    "approve" | "reject" | "sendBack" | null
  >(null);
  const [showSnackbar, setShowSnackbar] = useState(false);

  const isMobile = useMediaQuery("(max-width:600px)");
  const theme = useTheme();
  const navigate = useNavigate();

  const activityName =
    applicationData.activityList?.find(
      (a) => a.code === "1" && a.status === "InProgress"
    )?.activityName || "作業";

  // フォーム送信時に選択された決定タイプ（承認・却下・差戻し）に応じた処理を実行する
  const onSubmit = (data: CommentFormType) => {
    if (decisionType === "approve") {
      handleApproveVote(data.comment);
    } else if (decisionType === "reject") {
      handleRejectVote(data.comment);
    } else if (decisionType === "sendBack") {
      handleSendBack(data.comment);
    }
    setDecisionType(null);
    reset();
    setShowSnackbar(true);
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mx: isMobile ? 1 : 4,
            my: 2,
            p: "10px 40px",
            position: "center",
            border: "1px solid #ccc",
            borderRadius: "2px",
            backgroundColor: theme.palette.secondary.main,
          }}
        >
          <Typography variant="h3" sx={{ mt: 2 }}>
            {activityName}
          </Typography>
          <FormTextField
            name="comment"
            label="コメント（任意）"
            variant="outlined"
            multiline
            rows={4}
            sx={{
              backgroundColor: "#FFFFFF",
              width: "100%",
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "gray",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "primary",
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              my: 2,
              gap: isMobile ? 1 : 2,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            {" "}
            <Button
              variant="outlined"
              color="inherit"
              sx={{
                typography: "buttonMedium",
                color: "grey",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={() => setDecisionType("sendBack")}
              type="submit"
            >
              差戻
            </Button>
            <Button
              variant="outlined"
              color="primary"
              sx={{
                typography: "buttonMedium",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={() => setDecisionType("reject")}
              type="submit"
            >
              却下
            </Button>
            <Button
              variant="contained"
              color="primary"
              sx={{
                typography: "buttonMedium",
                width: isMobile ? "100%" : "auto",
              }}
              onClick={() => setDecisionType("approve")}
              type="submit"
            >
              承認
            </Button>
          </Box>

          <Snackbar
            open={showSnackbar}
            autoHideDuration={3000}
            onClose={() => setShowSnackbar(false)}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          >
            <Alert
              onClose={() => setShowSnackbar(false)}
              severity="success"
              sx={{ width: "100%" }}
            >
              コメントを送信しました。
            </Alert>
          </Snackbar>
        </Box>
      </form>
    </FormProvider>
  );
};
